import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { contactDetail, NEWS, fmtDT, fmtDay } from "@/lib/contactsApi";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { TestBadge } from "@/components/TestMode";
import { Button } from "@/components/ui/button";
import { UserCog } from "lucide-react";
import { AccountManager } from "@/components/contacts/AccountManager";
import { ROLE_LABEL, STATUS_LABEL } from "@/lib/access";
import { TrainerContact } from "@/components/contacts/TrainerContact";

const EVENT = { opt_in: "Opted in", opt_out: "Opted out", no_active_opt_in: "No active opt-in (box not ticked by seller)" };
const METHOD = { user: "ticked by seller", prechecked: "pre-checked only (not an opt-in)" };

function Row({ k, v, testId }) {
  return <div className="flex justify-between gap-3 py-1.5 border-b border-stone-100 text-sm"><span className="text-stone-500">{k}</span><span data-testid={testId} className="font-medium text-right break-all">{v || "—"}</span></div>;
}

export function ContactDetail({ email, onClose, onChanged }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [manage, setManage] = useState(false);
  useEffect(() => { setD(null); setManage(false); contactDetail(email).then(setD).catch(() => setD({ error: true })); }, [email]);
  const changed = (gone) => { onChanged?.(); if (gone) onClose(); else contactDetail(email).then(setD).catch(() => {}); };
  const n = d && NEWS[d.newsletter_status];
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="contact-detail" side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle data-testid="contact-detail-name">{d?.name || (email.startsWith("cr:") ? "Contact" : email)}</SheetTitle>
          <SheetDescription className="break-all">{d ? d.email || "No email" : email.startsWith("cr:") ? "" : email}</SheetDescription>
        </SheetHeader>
        {!d ? <p className="mt-4 text-sm text-stone-500">Loading…</p> : d.error ? <p className="mt-4 text-sm text-red-700">Could not load contact.</p> : (
          <div className="mt-4 space-y-5">
            <div>
              <Row k="Phone" v={d.phone} />
              <Row k="Roles" v={(d.contact_roles || []).map((r) => r[0].toUpperCase() + r.slice(1)).join(", ") || null} testId="contact-detail-roles" />
              <Row k="Account" v={d.registered ? `${ROLE_LABEL[d.role] || d.role} · ${STATUS_LABEL[d.status] || d.status}` : "No login account (contact only)"} testId="contact-detail-account" />
              <Row k="Horses" v={d.registered ? String(d.horses_count) : null} />
              <Row k="Registered" v={d.registered ? fmtDay(d.registered_at) : null} />
              <Row k="Last login" v={d.last_login_at ? fmtDT(d.last_login_at) : null} />
              <Row k="Newsletter" v={n?.[0]} testId="contact-detail-newsletter" />
              <Row k="Consent" v={d.consent_at ? `${fmtDT(d.consent_at)} · ${d.consent_source || ""}` : null} />
              {d.opted_out_at && <Row k="Opted out" v={fmtDT(d.opted_out_at)} />}
            </div>
            {d.trainer_contact_id && <TrainerContact d={d} onSaved={() => changed()} />}
            {d.user_id && (
              <section>
                <Button data-testid="manage-account-btn" onClick={() => setManage((m) => !m)} className="w-full h-11 rounded-full bg-stone-900 hover:bg-stone-800 text-white">
                  <UserCog className="h-4 w-4 mr-1.5" />{manage ? "Hide Account Management" : "Manage Account"}
                </Button>
                {manage && <div className="mt-3"><AccountManager uid={d.user_id} onChanged={changed} /></div>}
              </section>
            )}
            <section>
              <h3 className="text-sm font-semibold mb-1.5">Promotional consent history</h3>
              <ol data-testid="contact-consent-history" className="space-y-1 text-xs text-stone-700">
                {d.consent_history.length ? [...d.consent_history].reverse().map((h, i) => (
                  <li key={i} className="rounded-lg bg-stone-50 border border-stone-200 px-2.5 py-1.5"><span className="font-semibold">{EVENT[h.event] || h.event}</span> · {fmtDT(h.at)}
                    {(h.source || h.is_test) && <span data-testid={`consent-source-${i}`} className="text-stone-500"> · {h.channel === "website" ? "Website" : "Application"}{h.source && h.channel === "website" ? ` (${h.source})` : ""}{h.is_test ? " · TEST" : ""}</span>}
                    {h.consent_text && <div data-testid={`consent-text-${i}`} className="mt-0.5 text-stone-500 italic break-words">“{h.consent_text}”</div>}</li>
                )) : <li className="text-stone-500">No consent records.</li>}
              </ol>
            </section>
            <section>
              <h3 className="text-sm font-semibold mb-1.5">Horse applications ({d.applications.length})</h3>
              <ul className="space-y-2">
                {d.applications.map((a, i) => (
                  <li key={a.id}>
                    <button type="button" data-testid={`contact-app-${i}`} onClick={() => navigate(`/applications/${a.id}`)}
                      className="w-full text-left rounded-xl border border-stone-200 p-3 hover:bg-stone-50 min-h-[56px]">
                      <div className="flex items-center gap-1.5 font-semibold text-sm">{a.is_test && <TestBadge testId={`contact-app-test-${i}`} />}{a.horse_name || "Unnamed horse"}</div>
                      <div className="text-xs text-stone-500">{a.status.replace(/_/g, " ")} · submitted {fmtDay(a.submitted_at)}</div>
                      <div className="text-xs text-stone-500">Promotional: {a.promotional ? "opted in" : "no"}{a.promotional_method ? ` (${METHOD[a.promotional_method] || a.promotional_method})` : ""}</div>
                    </button>
                  </li>
                ))}
                {!d.applications.length && <li className="text-xs text-stone-500">No submitted applications.</li>}
              </ul>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
